import React, {useCallback, useState} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import getSelection from '@pluto/get-selection';
import {
    frameToSMPTE,
    FrameRate,
} from '@accurate-player/accurate-player-core';
import {
    ApTimelineMarker,
    ApTimelineMetadata,
    MarkerApi,
    Marker,
} from '@accurate-player/accurate-player-components-external';

import {getMarkerStyling, ThemeMarkerType} from '../../../Timeline/TimelineTheme';
import {
    TimelineRow,
    TimelineMarker,
    OnTimelineMarkerClick,
    MouseTouchEvent,
    OnTimelineMarkerEnter,
    OnTimelineMarkerLeave,
    TooltipMarkerOrUndefined,
} from '../../../Timeline/TimelineTypes';
import SubtitleTrack from './SubtitleTrack';
import {ToggleSelectedMarker, ToggleSelectedRow} from '../../../Timeline/Redux/TimelineActions';
import {EnableSubtitle, DisableSubtitle} from '../Redux/SubtitleTracksActions';

export interface SubtitleTrackContainerProps {
    subtitleTrackData: TimelineRow;
    enabled: boolean;
    selectedMarkerId: string;
    selectedRowId: string;
    frameRate: FrameRate;
    dropFrame: boolean;
    enableSubtitle: EnableSubtitle;
    disableSubtitle: DisableSubtitle;
    toggleSelectedMarker: ToggleSelectedMarker;
    toggleSelectedRow: ToggleSelectedRow;
}

export type SetTooltipAction = React.Dispatch<React.SetStateAction<TooltipMarkerOrUndefined>>;

export const handleSubtitleCueClick = curry((props: SubtitleTrackContainerProps, cue: TimelineMarker, api: MarkerApi, event: MouseTouchEvent) => {
    event.stopPropagation();
    const {
        subtitleTrackData,
        toggleSelectedMarker,
    } = props;
    const {
        identifier,
        type,
    } = subtitleTrackData;
    
    toggleSelectedMarker(cue.identifier, identifier, type);
});

export type CueEnter = {
    markers: TimelineMarker[],
    frameRate: FrameRate,
    dropFrame: boolean,
    setTooltip: SetTooltipAction,
}

export const getMarkerPosition = (api: MarkerApi, event: MouseTouchEvent): number[] => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const markerRect = api.position();
    
    const left = rect.left + markerRect.left;
    const top = rect.top + markerRect.top;
    const {width} = markerRect;
    
    return [left, top, width, rect.right];
};

const getMarkerSMPTE = (marker: TimelineMarker, frameRate: FrameRate, dropFrame: boolean): string[] => {
    const {
        numerator,
        denominator,
    } = frameRate;
    
    const frameRateValue = numerator / denominator;
    const start = frameToSMPTE(marker.start, frameRateValue, dropFrame);
    const end = frameToSMPTE(marker.end, frameRateValue, dropFrame);
    
    return [start, end];
};

export const getTimestampText = (marker: TimelineMarker, frameRate: FrameRate, dropFrame: boolean) => {
    const [start, end] = getMarkerSMPTE(marker, frameRate, dropFrame);
    const text = `${start} - ${end}`;
    
    return text;
};

const equalMarker = curry((a: Marker, b: TimelineMarker) => {
    return a.identifier === b.identifier;
});

export const handleCueEnter = curry((cueData: CueEnter, marker: Marker, api: MarkerApi, event: MouseTouchEvent) => {
    const {
        setTooltip,
        frameRate,
        dropFrame,
        markers,
    } = cueData;
    
    const compareMarker = equalMarker(marker);
    const cue = markers.find(compareMarker);
    
    if (!cue)
        return;
    
    const {text} = cue;
    const tooltipText = text || '';
    const headerText = getTimestampText(cue, frameRate, dropFrame);
    const [left, top, width, maxRight] = getMarkerPosition(api, event);
    
    setTooltip({
        text: tooltipText,
        headerText,
        left,
        top,
        width,
        maxRight,
    });
});

export const handleCueLeave = wrap((setTooltip: SetTooltipAction) => {
    setTooltip(undefined);
});

export type OnCueEvents = {
    onCueClick: OnTimelineMarkerClick,
    onCueEnter: OnTimelineMarkerEnter,
    onCueLeave: OnTimelineMarkerLeave,
};

export const handleSubtitleContainerChange = curry((onCueEvents: OnCueEvents, props: SubtitleTrackContainerProps, node: ApTimelineMetadata) => {
    if (node) {
        const {
            subtitleTrackData,
            selectedMarkerId,
            selectedRowId,
            enabled,
        } = props;
        const {
            onCueClick,
            onCueEnter,
            onCueLeave,
        } = onCueEvents;
        
        node.apMarkerClick = onCueClick;
        node.apMarkerMouseEnter = onCueEnter;
        node.apMarkerMouseLeave = onCueLeave;
        node.selection = getSelection(subtitleTrackData, selectedMarkerId, selectedRowId);
        node.markerStyle = getMarkerStyling(ThemeMarkerType.Captions, enabled);
    }
});

const useSubtitleContainerRef = (props: SubtitleTrackContainerProps, setTooltip: SetTooltipAction) => {
    const {
        frameRate,
        dropFrame,
        subtitleTrackData,
    } = props;
    const {markers} = subtitleTrackData;
    const onCueClick = handleSubtitleCueClick(props);
    const onCueEnter = handleCueEnter({
        setTooltip,
        frameRate,
        dropFrame,
        markers,
    });
    const onCueLeave = handleCueLeave(setTooltip);
    const onSubtitleContainerChange = handleSubtitleContainerChange({
        onCueClick,
        onCueEnter,
        onCueLeave,
    }, props);
    const setRef = useCallback(onSubtitleContainerChange, [onCueClick]);
    
    return [setRef];
};

const handleCuesChanged = curry((cues: TimelineMarker[], node: ApTimelineMarker) => {
    if (node) {
        node.data = cues;
    }
});

const useSubtitleRef = (props: SubtitleTrackContainerProps) => {
    const {markers} = props.subtitleTrackData;
    const onCuesChange = handleCuesChanged(markers);
    const setRef = useCallback(onCuesChange, [markers]);
    
    return [setRef];
};

export const onSubtitleRowClicked = wrap((subtitleTrackData: TimelineRow, toggleSelectedRow: ToggleSelectedRow) => {
    const {
        identifier,
        type,
    } = subtitleTrackData;
    
    toggleSelectedRow(identifier, type);
});

export const onToggleClicked = wrap((props: SubtitleTrackContainerProps, event: MouseEvent) => {
    event.stopPropagation();
    
    const {
        enabled,
        subtitleTrackData,
        enableSubtitle,
        disableSubtitle,
    } = props;
    const {identifier} = subtitleTrackData;
    
    if (enabled) {
        disableSubtitle(identifier);
    } else {
        enableSubtitle(identifier);
    }
});

export default function SubtitleTrackContainer(props: SubtitleTrackContainerProps) {
    const {
        subtitleTrackData,
        enabled,
        toggleSelectedRow,
        selectedRowId,
    } = props;
    const [tooltip, setTooltip] = useState<TooltipMarkerOrUndefined>(undefined);
    const [setSubtitleRef] = useSubtitleRef(props);
    const [setSubtitleContainerRef] = useSubtitleContainerRef(props, setTooltip);
    const handleSubtitleRowClicked = onSubtitleRowClicked(subtitleTrackData, toggleSelectedRow);
    const handleToggleClicked = onToggleClicked(props);
    
    return (
        <SubtitleTrack
            subtitleTrackData={subtitleTrackData}
            selectedRowId={selectedRowId}
            setSubtitleRef={setSubtitleRef}
            setSubtitleContainerRef={setSubtitleContainerRef}
            onRowClicked={handleSubtitleRowClicked}
            onToggleEnabled={handleToggleClicked}
            tooltipMarker={tooltip}
            enabled={enabled}
        />
    );
}
